if (!customElements.get('hero-slideshow')) {
  class HeroSlideshow extends HTMLElement {
    constructor() {
      super();
      this.track = this.querySelector('.hero-slideshow__track');
      this.slides = Array.from(this.querySelectorAll('.hero-slide'));
      this.prevButton = this.querySelector('.hero-slideshow__arrow--prev');
      this.nextButton = this.querySelector('.hero-slideshow__arrow--next');
      this.dots = Array.from(this.querySelectorAll('.hero-slideshow__dot'));
      this.currentIndex = 0;
      this.autoplayTimer = null;
      this.autoplaySpeed = 5000;
      this.isPointerDown = false;
      this.pointerStartX = 0;
      this.editorPaused = false;
    }

    connectedCallback() {
      if (this.slides.length < 2) return;

      this.onPrevClick = this.showPrevious.bind(this);
      this.onNextClick = this.showNext.bind(this);
      this.onKeydown = this.handleKeydown.bind(this);
      this.onMouseEnter = this.pauseAutoplay.bind(this);
      this.onMouseLeave = this.resumeAutoplay.bind(this);
      this.onFocusIn = this.pauseAutoplay.bind(this);
      this.onFocusOut = this.resumeAutoplay.bind(this);
      this.onPointerDown = this.handlePointerDown.bind(this);
      this.onPointerMove = this.handlePointerMove.bind(this);
      this.onPointerUp = this.handlePointerUp.bind(this);
      this.onBlockSelect = this.handleBlockSelect.bind(this);
      this.onBlockDeselect = this.handleBlockDeselect.bind(this);

      if (this.prevButton) this.prevButton.addEventListener('click', this.onPrevClick);
      if (this.nextButton) this.nextButton.addEventListener('click', this.onNextClick);

      this.dots.forEach((dot, index) => {
        dot.addEventListener('click', () => this.goToSlide(index));
      });

      this.addEventListener('keydown', this.onKeydown);
      this.addEventListener('mouseenter', this.onMouseEnter);
      this.addEventListener('mouseleave', this.onMouseLeave);
      this.addEventListener('focusin', this.onFocusIn);
      this.addEventListener('focusout', this.onFocusOut);

      this.track.addEventListener('pointerdown', this.onPointerDown);
      this.track.addEventListener('pointermove', this.onPointerMove);
      this.track.addEventListener('pointerup', this.onPointerUp);
      this.track.addEventListener('pointercancel', this.onPointerUp);

      document.addEventListener('shopify:block:select', this.onBlockSelect);
      document.addEventListener('shopify:block:deselect', this.onBlockDeselect);

      this.startAutoplay();
    }

    disconnectedCallback() {
      this.pauseAutoplay();
      document.removeEventListener('shopify:block:select', this.onBlockSelect);
      document.removeEventListener('shopify:block:deselect', this.onBlockDeselect);
    }

    startAutoplay() {
      if (this.editorPaused) return;
      this.pauseAutoplay();
      this.autoplayTimer = setInterval(() => this.showNext(), this.autoplaySpeed);
    }

    pauseAutoplay() {
      if (this.autoplayTimer) {
        clearInterval(this.autoplayTimer);
        this.autoplayTimer = null;
      }
    }

    resumeAutoplay() {
      if (!this.editorPaused) this.startAutoplay();
    }

    showNext() {
      this.goToSlide((this.currentIndex + 1) % this.slides.length);
    }

    showPrevious() {
      this.goToSlide((this.currentIndex - 1 + this.slides.length) % this.slides.length);
    }

    goToSlide(index) {
      if (index === this.currentIndex) return;

      const previousSlide = this.slides[this.currentIndex];
      const nextSlide = this.slides[index];

      previousSlide.classList.remove('is-active');
      previousSlide.setAttribute('aria-hidden', 'true');

      nextSlide.classList.add('is-active');
      nextSlide.setAttribute('aria-hidden', 'false');

      const lazyImg = nextSlide.querySelector('img[loading="lazy"]');
      if (lazyImg) lazyImg.loading = 'eager';

      if (this.dots[this.currentIndex]) {
        this.dots[this.currentIndex].classList.remove('is-active');
        this.dots[this.currentIndex].setAttribute('aria-selected', 'false');
      }
      if (this.dots[index]) {
        this.dots[index].classList.add('is-active');
        this.dots[index].setAttribute('aria-selected', 'true');
      }

      this.currentIndex = index;
    }

    handleKeydown(event) {
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        this.showPrevious();
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        this.showNext();
      }
    }

    handlePointerDown(event) {
      this.isPointerDown = true;
      this.pointerStartX = event.clientX;
      this.pauseAutoplay();
    }

    handlePointerMove(event) {
      if (!this.isPointerDown) return;
      event.preventDefault();
    }

    handlePointerUp(event) {
      if (!this.isPointerDown) return;
      this.isPointerDown = false;

      const deltaX = event.clientX - this.pointerStartX;
      const swipeThreshold = 40;

      if (deltaX > swipeThreshold) {
        this.showPrevious();
      } else if (deltaX < -swipeThreshold) {
        this.showNext();
      }

      this.resumeAutoplay();
    }

    handleBlockSelect(event) {
      const slide = this.querySelector(`.hero-slide[data-block-id="${event.detail.blockId}"]`);
      if (!slide) return;

      const index = this.slides.indexOf(slide);
      if (index === -1) return;

      this.editorPaused = true;
      this.pauseAutoplay();
      this.goToSlide(index);
    }

    handleBlockDeselect() {
      this.editorPaused = false;
      this.resumeAutoplay();
    }
  }

  customElements.define('hero-slideshow', HeroSlideshow);
}
